import { API_BASE_URL, API_ROUTES, HTTP_METHOD, HTTP_STATUS } from '../constants/api'
import {
  clearAuthSession,
  getAccessToken,
  getRefreshToken,
  parseAuthSession,
  readAuthSession,
  writeAuthSession,
} from './auth-storage'

export class ApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

type Json = Record<string, unknown>

type RequestOptions = {
  method: string
  body?: BodyInit | null
  asJson?: boolean
  auth?: boolean
  skipRefresh?: boolean
}

let refreshPromise: Promise<string | null> | null = null
const getInflight = new Map<string, Promise<unknown>>()

export async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    clearAuthSession()
    return null
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const raw = await requestRaw<Record<string, unknown>>(
          API_ROUTES.authRefresh,
          {
            method: HTTP_METHOD.post,
            body: JSON.stringify({ refreshToken }),
            asJson: true,
            auth: false,
            skipRefresh: true,
          },
        )
        const accessToken =
          typeof raw.accessToken === 'string' ? raw.accessToken : null
        const nextRefresh =
          typeof raw.refreshToken === 'string' ? raw.refreshToken : null
        if (!accessToken || !nextRefresh) {
          clearAuthSession()
          return null
        }

        const nextSession = parseAuthSession(
          JSON.stringify({
            accessToken,
            refreshToken: nextRefresh,
            user: raw.user,
          }),
        )
        const current = readAuthSession()
        if (nextSession) {
          writeAuthSession(nextSession)
        } else if (current) {
          writeAuthSession({
            ...current,
            accessToken,
            refreshToken: nextRefresh,
          })
        } else {
          clearAuthSession()
          return null
        }
        return accessToken
      } catch (error) {
        if (error instanceof ApiError && error.status === HTTP_STATUS.unauthorized) {
          clearAuthSession()
        }
        return null
      } finally {
        refreshPromise = null
      }
    })()
  }

  return refreshPromise
}

async function requestRaw<T>(
  path: string,
  options: RequestOptions,
): Promise<T> {
  const { method, body = null, asJson = true, auth = true } = options
  const headers: Record<string, string> = {
    Accept: 'application/json',
  }
  if (asJson) headers['Content-Type'] = 'application/json'
  if (auth) {
    const token = getAccessToken()
    if (token) headers.Authorization = `Bearer ${token}`
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      body,
      headers,
    })
  } catch {
    throw new ApiError('Network error — could not reach the server.', 0)
  }

  if (
    response.status === HTTP_STATUS.unauthorized &&
    auth &&
    !options.skipRefresh &&
    path !== API_ROUTES.authRefresh &&
    path !== API_ROUTES.authLogin
  ) {
    const nextToken = await refreshAccessToken()
    if (nextToken) {
      return requestRaw<T>(path, { ...options, skipRefresh: true })
    }
  }

  if (!response.ok) {
    throw new ApiError(await readError(response), response.status)
  }

  if (response.status === 204) return undefined as T
  const text = await response.text()
  return text ? (JSON.parse(text) as T) : (undefined as T)
}

async function readError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: unknown }
    if (Array.isArray(body.message)) return body.message.join(', ')
    if (typeof body.message === 'string') return body.message
  } catch {
    // fall through to generic message
  }
  return `Request failed (${response.status}).`
}

export const api = {
  get: <T>(path: string) => {
    const existing = getInflight.get(path)
    if (existing) return existing as Promise<T>
    const request = requestRaw<T>(path, { method: HTTP_METHOD.get }).finally(
      () => {
        getInflight.delete(path)
      },
    )
    getInflight.set(path, request)
    return request
  },
  post: <T>(path: string, body: Json) =>
    requestRaw<T>(path, {
      method: HTTP_METHOD.post,
      body: JSON.stringify(body),
    }),
  postPublic: <T>(path: string, body: Json) =>
    requestRaw<T>(path, {
      method: HTTP_METHOD.post,
      body: JSON.stringify(body),
      auth: false,
      skipRefresh: true,
    }),
  patch: <T>(path: string, body: Json) =>
    requestRaw<T>(path, {
      method: HTTP_METHOD.patch,
      body: JSON.stringify(body),
    }),
  del: <T>(path: string) =>
    requestRaw<T>(path, { method: HTTP_METHOD.delete }),
}
